import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Button } from "@fluentui/react-components";

import TableComponent from "../../components/table/TableComponent";
import { AdminService } from "../../KulcsUzletSvc";
import { useStylesMainPage } from "../../styles/MainPageStyle";
import { TABLE } from "../../utils/Enums";

export interface CouponDetails {
  couponCode: string;
  couponType: number;
  modulId: string | null;
  modul: any | null;
  creationDateTime: string;
  discount: number;
  validTo: string;
  validDays: number;
  expiredDate: string;
  traderId: string;
  trader: any | null;
  creatorId: string;
  creator: any | null;
  modifierId: string;
  modifier: any | null;
  activatedByAccountId: string | null;
  account: any | null;
  id: string;
  rowVersion: string;
}

export interface UsedCouponDto {
  code: string;
  id: string;
  partnerCode: string;
  partnerState: string;
  transactionState: string;
  usedDate: string | null;
  coupon: CouponDetails;
  modulName: string | null;
  monthPrice: number | null;
  discountPercentage: number;
  isActive: boolean;
  usedCount: number | null;
}

export default function UsedCoupons() {
  const styles = useStylesMainPage();
  const [data, setData] = useState<null | UsedCouponDto[]>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [merchantName, setMerchanName] = useState("");
  const { id } = useParams();
  const navigate = useNavigate();

  const navigateBack = () => {
    navigate(-1);
  };

  const getData = useCallback(
    async (applicationUserId: string) => {
      setIsLoading(true);

      try {
        // 1) Load coupons created by this merchant
        const couponsRes = await AdminService.getAdminApplicationusercoupons(
          applicationUserId
        );

        const usagesAggregated: UsedCouponDto[] = [];

        // 2) For each coupon, fetch usage records filtered by coupon code
        for (const r of couponsRes ?? []) {
          const coupon = (r as any).coupon as any;
          const couponCode = coupon?.couponCode ?? null;
          if (!couponCode) continue;

          try {
            const usagesRes = await AdminService.getAdminUsedcoupons(
              couponCode
            );
            const usageItems = (usagesRes && (usagesRes as any).items) || [];

            // Map usage items to UsedCouponDto entries
            for (const ui of usageItems as any[]) {
              const usedCouponDto: UsedCouponDto = {
                code: couponCode,
                id: ui.id ?? ui.usageId ?? `${couponCode}-${ui.usageDate}`,
                partnerCode: ui.partnerCode ?? "",
                partnerState: ui.partnerState ?? "",
                transactionState: ui.transactionState ?? "",
                usedDate: ui.usageDate ?? null,
                coupon: coupon as CouponDetails,
                modulName: ui.modulName ?? coupon?.modul?.name ?? null,
                monthPrice: ui.monthPrice ?? ui.discountedCost ?? null,
                discountPercentage: coupon?.discount ?? ui.discount ?? 0,
                isActive: true,
                usedCount: 1,
              };
              usagesAggregated.push(usedCouponDto);
            }
          } catch (innerErr) {
            console.warn(
              `Failed to load usages for coupon ${couponCode}`,
              innerErr
            );
          }
        }

        setData(usagesAggregated);

        // Lekérjük a kereskedő nevét is
        const merchantData = await AdminService.getAdminApplicationUser(
          applicationUserId
        );
        setMerchanName(merchantData?.fullName ?? "");
      } catch (e) {
        console.error("Adatlekérési hiba:", e);
        setData([]);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );
  useEffect(() => {
    if (id) {
      getData(id);
    }
  }, [id, getData]);

  return (
    <div className={styles.main}>
      <div
        style={{
          textAlign: "center",
          width: "100%",
          fontWeight: "bold",
          fontSize: "20px",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {`${merchantName} felhasznált kuponjai`}
      </div>
      <div
        style={{
          alignItems: "center",
          justifyContent: "left",
          display: "flex",
          gap: "6px",
          marginBottom: "12px",
        }}
      >
        <Button onClick={navigateBack}>Vissza</Button>
        <Button
          appearance="primary"
          // Az id-t használjuk az új kupon létrehozásához vezető útvonalban
          onClick={() => navigate(`/admin/trader/${id}/coupon/create`)}
        >
          Új kupon
        </Button>
      </div>
      <TableComponent
        type={TABLE.USEDCOUPONS}
        data={data ?? []}
        isLoading={isLoading}
      />
    </div>
  );
}
